import { OutlookAttendeePayload } from "../../../modules/order/pages/components/bookingMessaging"

/**
 * Marks an attendee that was imported from the Outlook meeting (vs. manually added by the user).
 * UI-only: it is used to remove exactly the Outlook-sourced entries when the opt-in toggle is turned off,
 * and is stripped before the attendees are submitted to the API.
 */
export const OUTLOOK_ATTENDEE_SOURCE = "outlook" as const
export type AttendeeSource = typeof OUTLOOK_ATTENDEE_SOURCE

type TaggableAttendee = Readonly<{
    name: string
    company: string
    isExternal: boolean
    source?: AttendeeSource
}>

export function isOutlookSourced(attendee: TaggableAttendee): boolean {
    return attendee.source === OUTLOOK_ATTENDEE_SOURCE
}

/** Strips the UI-only `source` tag before submitting to the API. */
export function stripAttendeeSource<T extends TaggableAttendee>(attendees: ReadonlyArray<T>): Omit<T, "source">[] {
    return attendees.map(({ source: _source, ...rest }) => rest)
}

/** Maps the postMessage payload from the add-in into tagged, manager-shaped attendees. */
export function toOutlookSourcedAttendees(
    payload: ReadonlyArray<OutlookAttendeePayload>,
): Array<{ name: string; company: string; isExternal: boolean; source: AttendeeSource }> {
    return payload.map((a) => ({
        name: a.name,
        company: a.company ?? "",
        isExternal: a.isExternal,
        source: OUTLOOK_ATTENDEE_SOURCE,
    }))
}

function normalizeName(name: string): string {
    return name.trim().toLowerCase()
}

/**
 * Reconciles the participant list with the current Outlook attendees (used both on the manual toggle-on and
 * on the edit-mode eager load). Drops any previously Outlook-tagged entry AND any untagged entry whose name
 * matches a current Outlook attendee (so re-opening a saved order does not duplicate them — saved attendees
 * carry no provenance), plus the empty placeholder row; then appends the fresh, tagged Outlook attendees.
 */
export function reconcileImportedAttendees(
    current: ReadonlyArray<TaggableAttendee>,
    payload: ReadonlyArray<OutlookAttendeePayload>,
): TaggableAttendee[] {
    const outlookNames = new Set(payload.map((a) => normalizeName(a.name)))
    const manual = current.filter(
        (a) => !isOutlookSourced(a) && !outlookNames.has(normalizeName(a.name)) && (a.name || a.company),
    )
    return [...manual, ...toOutlookSourcedAttendees(payload)]
}
